'use client'

import dynamic from 'next/dynamic'
import { useMemo, useState } from 'react'
import { useField, useFormFields } from '@payloadcms/ui'
import { useMediaUrls } from './useMediaUrl'
import type { TourScene, TourHotspot, HotspotType } from './types'

const VirtualTourViewer = dynamic(
  () => import('./VirtualTourViewer').then(m => m.VirtualTourViewer),
  { ssr: false },
)

const toggleStyle: React.CSSProperties = {
  padding: '8px 14px', fontSize: 12, fontWeight: 600, borderRadius: 4,
  border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer',
}

type RowsField = { rows?: { id: string }[] }

export function TourPreviewField({ path }: { path: string }) {
  const basePath = path.replace(/\.preview$/, '')
  const scenesPath = `${basePath}.scenes`
  const startScenePath = `${basePath}.startScene`

  const [open, setOpen] = useState(false)

  const { rows: sceneRows } = useField<unknown>({ path: scenesPath, hasRows: true })
  const allFields = useFormFields(([fields]) => fields)
  const startScene = allFields?.[startScenePath]?.value as string | undefined

  const rawScenes = useMemo(() => {
    return (sceneRows || []).map((r, i) => {
      const rowId = (r as { id: string }).id
      const get = (name: string) => allFields?.[`${scenesPath}.${i}.${name}`]?.value
      const hotspotsEntry = allFields?.[`${scenesPath}.${i}.hotspots`] as RowsField | undefined
      const hotspotRows = hotspotsEntry?.rows || []
      const hotspots: TourHotspot[] = hotspotRows.map((hr, hi) => {
        const hget = (name: string) => allFields?.[`${scenesPath}.${i}.hotspots.${hi}.${name}`]?.value
        return {
          id: hr.id,
          yaw: (hget('yaw') as number) ?? 0,
          pitch: (hget('pitch') as number) ?? 0,
          type: (hget('type') as HotspotType) || 'scene',
          targetScene: hget('targetScene') as string | undefined,
          label: hget('label') as string | undefined,
          infoText: hget('infoText') as string | undefined,
          linkUrl: hget('linkUrl') as string | undefined,
        }
      })
      return {
        id: rowId,
        title: (get('title') as string) || `Scene ${i + 1}`,
        description: get('description') as string | undefined,
        panoramaId: get('panorama') as number | undefined,
        thumbnailId: get('thumbnail') as number | undefined,
        hotspots,
      }
    })
  }, [sceneRows, allFields, scenesPath])

  const mediaIds = useMemo(() => {
    if (!open) return []
    const ids = new Set<number>()
    rawScenes.forEach(s => {
      if (s.panoramaId) ids.add(s.panoramaId)
      if (s.thumbnailId) ids.add(s.thumbnailId)
    })
    return Array.from(ids)
  }, [rawScenes, open])

  const urlMap = useMediaUrls(mediaIds)

  const scenes: TourScene[] = useMemo(() => {
    return rawScenes
      .filter(s => s.panoramaId && urlMap[s.panoramaId])
      .map(s => ({
        id: s.id,
        title: s.title,
        description: s.description,
        panoramaUrl: urlMap[s.panoramaId as number] as string,
        thumbnailUrl: s.thumbnailId ? urlMap[s.thumbnailId] || undefined : undefined,
        hotspots: s.hotspots,
      }))
  }, [rawScenes, urlMap])

  return (
    <div style={{ marginBottom: 12 }}>
      <button type="button" onClick={() => setOpen(v => !v)} style={toggleStyle}>
        {open ? 'Hide tour preview' : 'Preview complete tour'}
      </button>
      {open && (
        scenes.length === 0 ? (
          <div style={{ padding: 16, fontSize: 12, color: '#9a9a9a' }}>
            Add at least one scene with a panorama to preview the tour.
          </div>
        ) : (
          <div style={{ marginTop: 10, height: 480, border: '1px solid #d0d0d0', borderRadius: 6, overflow: 'hidden' }}>
            <VirtualTourViewer scenes={scenes} startScene={startScene} />
          </div>
        )
      )}
    </div>
  )
}
